// @flow
import { groupItemBy } from '.'

export function getPercentageAsNumber (dividend: number, divisor: number) : number {
  return dividend !== 0 && divisor !== 0
    ? Math.floor((dividend / divisor) * 100)
    : 0
}

export function getPercentage (dividend: number, divisor: number) : string {
  return dividend !== 0 && divisor !== 0
    ? Math.floor((dividend / divisor) * 100).toString()
    : '0'
}

export function getCompletedQuestsCount (quests: Array<Object>, userQuests: Array<Object>) : number {
  // find user level quests count where the quest is completed
  return userQuests.filter((uq) => {
    return quests.find((q) => {
      return q.id === uq.quest_id && uq.completed_at
    })
  }).length
}

export function getCompletedQuestsStats (quests: Array<Object>, userQuests: Array<Object>) : string {
  return `${getCompletedQuestsCount(quests, userQuests)} <span>/</span> ${quests.length}`
}

export function getProgress (quests: Array<Object>, userQuests: Array<Object>) : string {
  return getPercentage(getCompletedQuestsCount(quests, userQuests), quests.length)
}

export function getRequiredQuestProgress (level: Object, userLevel: Object) : string {
  return userLevel && userLevel.required_quests_completed_count
    ? getPercentage(userLevel.required_quests_completed_count, level.required_level_quests_count)
    : '0'
}

export function getOptionalQuestsCount (level: Object) : number {
  return level.quests_count - level.required_level_quests_count
}

export function getChoiceQuestProgress (level: Object, userLevel: Object) : string {
  return userLevel && userLevel.optional_quests_completed_count
    ? getPercentage(userLevel.optional_quests_completed_count, getOptionalQuestsCount(level))
    : '0'
}

export function areLevelMinimumChoiceQuestsComplete (level: Object, userLevel: Object) : boolean {
  return userLevel.optional_quests_completed_count >= level.minimum_optional_quests_required_for_completion
}

export function isLevelCompleted (level: Object, userLevel: Object) : boolean {
  return level.quests_count > 0 &&
    (userLevel.required_quests_completed_count + userLevel.optional_quests_completed_count) === level.quests_count
}

export function areLevelMinimumRequirementsMet (level: Object, userLevel: Object) : boolean {
  if (level.minimum_quests_required_for_completion === 0 && level.minimum_optional_quests_required_for_completion === 0) {
    return false
  }
  return userLevel.required_quests_completed_count >= level.required_level_quests_count &&
    userLevel.optional_quests_completed_count >= level.minimum_optional_quests_required_for_completion
}

export function getIndexOfLowestActiveLevel (courseLevels: Array<Object>, userLevels: Array<Object>) : number {
  return courseLevels.findIndex((level) => {
    let ul = userLevels.find((userLevel) => {
      return userLevel.level_id === level.id
    })
    return ul ? (!ul.locked && ul.quests_started_count > 0 && !isLevelCompleted(level, ul)) : false
  })
}

export function getCourseSumOfRequiredQuests (courseLevels: Array<Object>) : number {
  return courseLevels.reduce(function (a, b) {
    return a + b.minimum_quests_required_for_completion
  }, 0)
}

export function getCourseSumOfCompletedRequiredQuests (courseLevels: Array<Object>, userLevels: Array<Object>) : number {
  return userLevels.reduce(function (a, b) {
    const level = courseLevels.find((l) => {
      return l.id === b.level_id
    })

    const minChoiceQuestCount = level ? level.minimum_optional_quests_required_for_completion : 0

    // get the number of completed choice quests, less than or equal to the "required" amount of choice quests
    const requiredChoiceComplete = b.optional_quests_completed_count <= minChoiceQuestCount ? b.optional_quests_completed_count : minChoiceQuestCount

    return a + (b.required_quests_completed_count + requiredChoiceComplete)
  }, 0)
}

export function getCourseLevelYPosition (index: number) : number {
  let y = 0
  switch (index % 10) {
    case 0 :
    case 6 :
      y = 380
      break
    case 1 :
    case 5 :
      y = 400
      break
    case 2 :
    case 4 :
    case 7 :
    case 9 :
      y = 290
      break
    case 3 :
    case 8 :
      y = 230
      break
  }

  return y
}

export function groupLevelSequences (array: Array<Object>) : Array {
  return array.reduce((result, value, index, collection) => {
    if (value.mastery) {
      result.push(['M'])
    } else {
      if ((index > 0 && value.number - collection[index - 1].number === 1) && !collection[index - 1].mastery) {
        let group = result.last()
        group.push(value.number)
      } else {
        result.push([value.number])
      }
    }
    return result
  }, [])
}

export function getLockedLevels (requirements: Array<Object>) : string {
  const levelGroups = groupLevelSequences(requirements)
  let requiredLevels = ''

  levelGroups.forEach((lg) => {
    if (lg.length > 1) {
      requiredLevels += ', ' + lg[0] + '-' + lg[lg.length - 1]
    } else {
      requiredLevels += ', ' + lg[0]
    }
  })

  return requiredLevels.replace(',', '')
}

export const groupEnrollmentsByMasterCourses = (masterCourses: Array<Object>) => (
  groupItemBy(masterCourses, 'master_course.id')
)

export const mapMasterCourseEnrollmentsObject = ({
  masterCourses,
  gradeLevels,
  academicYears,
  excludeNonAcademicYearCourses,
  frameworkSubjects
}: Object) => {
  let mapped = Object.keys(masterCourses).map(mcId => {
    const enrollmentData = masterCourses[mcId][0]
    return {
      masterCourse: enrollmentData.master_course,
      active: masterCourses[mcId].some(enrollment => enrollment.course_section.active),
      gradeLevel: gradeLevels.find(gl => gl.id === enrollmentData.master_course.grade_level_id),
      academicYear: academicYears.find(
        ay => ay.id === enrollmentData.master_course.academic_year_id
      ),
      enrollments: masterCourses[mcId],
      frameworkSubject: frameworkSubjects.find(
        fs => fs.id === enrollmentData.master_course.framework_subject_id
      )
    }
 })

 if (excludeNonAcademicYearCourses) {
  mapped = mapped.filter(mc => mc.academicYear !== undefined)
 }

 return mapped
}

export const mapCourseSectionPropToEnrollments = (enrollments: Array<Object>) => {
  return enrollments.map(enrollment => ({
    ...enrollment,
    course_section: {
      id: enrollment.id,
      name: enrollment.name,
      active: enrollment.active,
      start_at: enrollment.start_at,
      end_at: enrollment.end_at,
      academic_term_id: enrollment.academic_term_id
    }
  }))
}

export const isInFuture = (date) => {
  return date >= Math.floor(Date.now() / 1000)
}

export const getMasterCourseSelectOptionsFromEnrollments = (enrollments: Array<Object>) => (
  enrollments.map(ce => ({
    ...ce.masterCourse,
    active: ce.active || ce.enrollments.some(e => e.course_section.active),
    future: ce.future || ce.enrollments.some(e => e.course_section.future),
    gradeLevel: ce.gradeLevel,
    academicYear: ce.academicYear
  }))
)

export const getFilteredMasterCoursesFromEnrollments = ({
  masterCourseFilterId,
  courseEnrollments
}: Object) => (
  masterCourseFilterId === -1
    ? courseEnrollments.filter(
      ce => ce.enrollments.some(e => e.course_section.active)
    )
  : masterCourseFilterId === -2
    ? courseEnrollments.filter(
      ce => ce.enrollments.some(e => e.course_section.future)
    )
  : courseEnrollments.filter(
    ce => ce.masterCourse.id === masterCourseFilterId
  )
)

export const getQuestMasterCourses = (courseEnrollments: Array<Object>) => {
  return courseEnrollments.filter(enrollment => !enrollment.masterCourse.non_quest_course)
}

export const getNonQuestMCFullData = (courseEnrollments: Array<Object>) => {
  return courseEnrollments.filter(enrollment => enrollment.masterCourse.non_quest_course)
}

export const getNonQuestMasterCourses = (courseEnrollments: Array<Object>) => {
  const courses = courseEnrollments
    .filter(ce => ce.masterCourse.non_quest_course)
    .map(ce => {
      const enrollmentsWithMeetingLinks = ce.enrollments.filter(
        e => e.course_section.meeting_link
      )
      const meetingLinks = {
        id: ce.masterCourse.id,
        meeting_links: enrollmentsWithMeetingLinks.map(e => ({
          course_name: e.course.name,
          meeting_link: e.course_section.meeting_link,
          section_name: e.course_section.name
        }))
      }

      return ({
        ...ce.masterCourse,
        meetingLinks
      })
    })

    return courses
}

export const getMasterCourseFromSection = (usercourses, courseSectionId) =>
  [...usercourses.currentUserCourses, ...usercourses.pastUserCourses].find(
    enrollment => enrollment.course_section.id === courseSectionId
  )?.master_course
